// src/prototype/data/adapters/generatedRationaleRows.ts
// ============================================================
// GENERATED FIXTURE → BQ-SHAPED ROWS
//
// The bridge between the wave-1 generated fixtures (src/prototype/data/fixtures/generated,
// emitted by scripts/generate-demo-dataset/generate.mjs) and the row shapes the adapter and
// the hub tabs already consume. Spec: docs/DVA_Demo_Dataset_Generation_Spec_v1.md v1.2.
//
// Tier A → full pattern: every measure carries evidence, and the personalization delta card
//          renders where a stage p4/p5_delta row existed.
// Tier B → lighter: no delta card, evidence only on the top 3 measures per dimension by score.
// Weights → REAL warehouse weight_label renders on ANY tier (spec v1.2 §4). Only invented weights
//          are barred. Prededup-sourced pages show none because that table has no weight column.
// Tier C → no module at all; useCaseContentMock renders the summary line instead.
// ============================================================

import { GENERATED_RATIONALE } from '../fixtures/generated';
import type { GeneratedUcRationale } from '../fixtures/generated/types';
import type {
  MeasureResultRow,
  MeasureRationalizationRow,
  RationaleProvenanceRow,
  PersonalizationDeltaRow,
  EvidenceType,
} from './useCaseContentTypes';

export { GENERATED_RATIONALE };

/** Warehouse source_type → the evidence kind the UI icons. Unknown values fall back to inference. */
const EVIDENCE_KIND: Record<string, EvidenceType> = {
  llm: 'inference',
  llm_generated: 'inference',
  inferred: 'inference',
  survey: 'survey',
  document: 'document',
  benchmark: 'benchmark',
  'apdva-prededup': 'inference',
};

const STAGE_PROJECT = 'dva-stage';
const APDVA_PROJECT = 'cs-poc-pr0n8qqumo0fl7jqn1n8qhk';

export function generatedFor(oppId: string): GeneratedUcRationale | undefined {
  return GENERATED_RATIONALE[oppId];
}

/**
 * Build measure_results + client_measure_rationalizations rows from a generated fixture.
 * `useCaseId` is the id the rest of the app keys on (stage id for stage-sourced rows).
 */
export function buildGeneratedRows(
  g: GeneratedUcRationale,
  clientId: string,
  useCaseId: number,
): {
  measures: MeasureResultRow[];
  rationalizations: MeasureRationalizationRow[];
  provenance: RationaleProvenanceRow;
  personalizationDelta?: PersonalizationDeltaRow;
} {
  const isStage = g.provenance.source === 'stage';

  const measures: MeasureResultRow[] = g.measures.map((m) => {
    const kind = EVIDENCE_KIND[(m.sourceType ?? '').toLowerCase()] ?? 'inference';
    // Evidence is the real row provenance, never invented: the rationale itself, plus the
    // basis on which its score was formed. Tier B carries it only on the capped measures.
    const evidence = m.evidence
      ? [
          {
            type: kind,
            src: isStage
              ? `${STAGE_PROJECT} · client_measure_rationalizations · uc ${g.provenance.stageUseCaseId} · source_type ${m.sourceType}`
              : `${APDVA_PROJECT} · measure_rationalizations_backup_before_dedup · ${g.provenance.batch}`,
            excerpt: m.text,
          },
          {
            type: 'calculation' as EvidenceType,
            src: isStage
              ? `Stage scoring · measure ${m.id}${m.weightLabel ? ` · weighted ${m.weightLabel}` : ''}`
              : `client-200 client_measure_rationalizations · median of duplicate rows`,
            excerpt: isStage
              ? `Stage score ${m.score} of 5${m.weightLabel ? `, weighted ${m.weightLabel} in the client-${clientId} run` : ''}.`
              : `Evaluation score ${(m.score ?? 0).toFixed(1)} of 100 — the median across this measure's client-200 rows.`,
          },
        ]
      : [];

    return {
      client_id: clientId,
      use_case_id: useCaseId,
      measure_id: `m${m.id}`,
      dimension: m.dim,
      measure_name: m.name,
      // null for a gap — the UI renders the honest state instead of a number.
      raw_score: m.score,
      evaluated: m.evaluated,
      score_scale: m.scale,
      // Neither warehouse supplies a per-measure confidence for these rows.
      confidence_level: null,
      evidence_json: evidence,
      scoring_version: 'v2_axis_scoring',
    };
  });

  const rationalizations: MeasureRationalizationRow[] = g.measures.map((m) => ({
    client_id: clientId,
    use_case_id: useCaseId,
    measure_id: `m${m.id}`,
    rationalization_text: m.text,
    score: m.score ?? 0,
    // Real warehouse weight, any tier (spec v1.2 §4). Undefined only when the source has none.
    weight_label: (m.weightLabel ?? undefined) as MeasureRationalizationRow['weight_label'],
    source_type: m.sourceType ?? undefined,
  }));

  const provenance: RationaleProvenanceRow = isStage
    ? {
        project: STAGE_PROJECT,
        dataset: 'dva_dataset',
        table: 'client_measure_rationalizations',
        clientId,
        useCaseId,
        correlationId: `generated·${g.provenance.demoOppId}`,
        contractVersion: g.personalizationDelta?.contractVersion ?? 'stage-v1',
        extractedAt: g.provenance.extractedAt,
      }
    : {
        project: APDVA_PROJECT,
        dataset: 'dva_dataset',
        table: 'measure_rationalizations_backup_before_dedup',
        clientId,
        useCaseId,
        correlationId: g.provenance.batch ?? 'apdva-prededup',
        contractVersion: 'apdva-prededup-v1',
        extractedAt: g.provenance.extractedAt,
      };

  return {
    measures,
    rationalizations,
    provenance,
    personalizationDelta: g.personalizationDelta
      ? ({ ...g.personalizationDelta } as PersonalizationDeltaRow)
      : undefined,
  };
}
